import Phaser from 'phaser';
import { NEON_PALETTE } from '@/utils/ColorUtils';

interface StrobeSource {
  worldX: number;
  worldY: number;
}

/**
 * Controls strobe light flashes at each equipment position.
 * On each beat, fires a bright flash with colored glow that decays.
 */
export class StrobeController {
  private scene: Phaser.Scene;
  private flashRects: Phaser.GameObjects.Rectangle[] = [];
  private glowRects: Phaser.GameObjects.Rectangle[] = [];
  private sources: StrobeSource[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  setSources(sources: StrobeSource[]): void {
    // Clean up old rects
    for (const rect of this.flashRects) rect.destroy();
    for (const rect of this.glowRects) rect.destroy();
    this.flashRects = [];
    this.glowRects = [];
    this.sources = sources;

    for (const src of sources) {
      // Wider colored glow
      const glow = this.scene.add.rectangle(
        src.worldX, src.worldY - 20,
        200, 140,
        0xff00ff, 0
      );
      glow.setBlendMode(Phaser.BlendModes.ADD);
      glow.setDepth(399);
      this.glowRects.push(glow);

      // Bright white core flash
      const rect = this.scene.add.rectangle(
        src.worldX, src.worldY - 20,
        140, 100,
        0xffffff, 0
      );
      rect.setBlendMode(Phaser.BlendModes.ADD);
      rect.setDepth(400);
      this.flashRects.push(rect);
    }
  }

  /** Fire strobe flash on beat */
  onBeat(beatCount: number): void {
    const color = NEON_PALETTE[beatCount % NEON_PALETTE.length];

    for (let i = 0; i < this.flashRects.length; i++) {
      // White core flash
      const rect = this.flashRects[i];
      rect.setAlpha(0.9);
      this.scene.tweens.add({
        targets: rect,
        alpha: 0,
        duration: 80,
        ease: 'Power3',
      });

      // Colored glow
      const glow = this.glowRects[i];
      glow.setFillStyle(color);
      glow.setAlpha(0.5);
      this.scene.tweens.add({
        targets: glow,
        alpha: 0,
        duration: 200,
        ease: 'Sine.easeOut',
      });
    }
  }

  destroy(): void {
    for (const rect of this.flashRects) rect.destroy();
    for (const rect of this.glowRects) rect.destroy();
    this.flashRects = [];
    this.glowRects = [];
  }
}
